'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { StepsList } from '../components/StepsList';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useSignupSponsor } from '../hooks/useSignupSponsor';
import { useStepHeading } from '../hooks/useStepHeading';

function ContextContent({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation('signup');
  const title = t('context.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();
  return (
    <SignupShell>
      <div className="signup-step" data-testid="signup-step">
        <PageTitle title={title} description={t('context.description')} headingRef={headingRef} />
        <StepsList
          label={t('context.stepsLabel')}
          steps={[t('context.steps.focus'), t('context.steps.eligibility'), t('context.steps.app')]}
          currentIndex={0}
        />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            onPress={onContinue}
            actionId="signup.context-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

function ContextStep() {
  const { clientId, subClientId, directToReg } = useLegacySignupParams();
  // Resolved here so later steps read it from the cache. v9 shows nothing
  // about the sponsor on this screen, so a failed lookup changes nothing.
  useSignupSponsor(clientId, subClientId);
  const { goToNextStep } = useSignupNavigation();
  useEffect(() => {
    if (directToReg) goToNextStep('context');
  }, [directToReg, goToNextStep]);
  if (directToReg) return null;
  return <ContextContent onContinue={() => goToNextStep('context')} />;
}

/** `/context/`: what registration involves (v9 1.1). */
export function ContextStepScreen() {
  return <ContextStep />;
}
